// The branch search page (spec/dashboard.md section 6.6): `…/branches/{branch}/search?q=&template=&tag=` with one
// section per kind — documents in a grid (ID, Label, Template, Tags, Version and a row menu), templates, localization
// strings, assets, products — each from its list endpoint with `search`, paged on the server with Load more. The text
// goes to `q` as you type after a 300 ms pause, Enter at once (ui/useDebounced.ts).
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Asset, Document, LocalizationString, Page, Product, Template } from '../api/types';
import { baseOf, cloneDocument } from '../content/actions';
import { productLabelOf } from '../content/ContentContext';
import { stageDeletion } from '../content/DocumentActions';
import { DocumentEditorHost } from '../content/DocumentEditor';
import { KIND_LABEL } from '../content/schema';
import { routes } from '../shell/routes';
import { EmptyState } from '../ui/Banner';
import { Button, IconButton } from '../ui/Button';
import { Badge, draftTone } from '../ui/Chip';
import { DataGrid } from '../ui/DataGrid';
import { AlertCircle, FileText, Images, Languages, Layers, MoreVertical, Search as SearchIcon, ShoppingBag } from '../ui/icons';
import { Select, TextInput } from '../ui/inputs';
import { useUrlParam } from '../ui/Modal';
import { useSearchBox } from '../ui/useDebounced';
import { Menu } from '../ui/Popover';
import { useWorkspaceVersion } from '../workspace/react';
import { entityKey, isTempId } from '../workspace/Workspace';
import { useSchemaEnv } from './schema/common';
import './schema/schema.css';

const PAGE = 25;
/** the documents grid's rows per page and server page: one request fills a page */
const DOC_PAGE = 50;

interface Paged<T> {
  items: T[];
  hasMore: boolean;
  loading: boolean;
  error: ApiError | null;
  loadMore: () => void;
}

/** One server-paged list: the first page whenever `key` changes, the next on Load more. */
function usePaged<T>(key: string | null, fetchPage: (cursor: string | null) => Promise<Page<T>>): Paged<T> {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const generation = useRef(0);
  const load = useCallback(async (after: string | null, replace: boolean) => {
    const mine = ++generation.current;
    setLoading(true);
    try {
      const page = await fetchRef.current(after);
      if (mine !== generation.current) return;
      setItems((prev) => (replace ? page.items : [...prev, ...page.items]));
      setCursor(page.nextCursor);
      setError(null);
    } catch (e) {
      if (mine !== generation.current) return;
      setError(e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), ''));
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (key === null) {
      generation.current++;
      setItems([]);
      setCursor(null);
      setError(null);
      setLoading(false);
      return;
    }
    setItems([]);
    setCursor(null);
    void load(null, true);
  }, [key, load]);
  return { items, hasMore: cursor !== null, loading, error, loadMore: () => void load(cursor, false) };
}

export function SearchPage() {
  return (
    <DocumentEditorHost>
      <Search />
    </DocumentEditorHost>
  );
}

function Search() {
  const { client, path, catalog, workspace, org, gameSlug, branch, canWrite, toasts, navigate } = useSchemaEnv();
  const version = useWorkspaceVersion(workspace);
  const qParam = useUrlParam('q');
  const docParam = useUrlParam('doc');
  const q = qParam.value ?? '';
  const templateFilter = useUrlParam('template').value;
  const tagFilter = useUrlParam('tag').value;
  const searchBox = useSearchBox(q, (text) => qParam.set({ q: text || null }));
  const head = workspace.head ?? '';

  const docsKey = q || templateFilter || tagFilter ? `docs|${branch}|${q}|${templateFilter ?? ''}|${tagFilter ?? ''}|${head}` : null;
  const docs = usePaged<Document>(docsKey, (cursor) => client.data(ops.listDocuments, { path, query: { search: q || undefined, template: templateFilter ?? undefined, tag: tagFilter ?? undefined, limit: DOC_PAGE, cursor: cursor ?? undefined } }));
  useEffect(() => {
    if (docs.items.length > 0) catalog.resolver.seed(docs.items);
  }, [docs.items, catalog.resolver]);
  const textKey = (kind: string) => (q ? `${kind}|${branch}|${q}|${head}` : null);
  const templates = usePaged<Template>(textKey('templates'), (cursor) => client.data(ops.listTemplates, { path, query: { search: q, limit: PAGE, cursor: cursor ?? undefined } }));
  const strings = usePaged<LocalizationString>(textKey('strings'), (cursor) => client.data(ops.listLocalizationStrings, { path, query: { search: q, limit: PAGE, cursor: cursor ?? undefined } }));
  const assets = usePaged<Asset>(textKey('assets'), (cursor) => client.data(ops.listAssets, { path, query: { search: q, limit: PAGE, cursor: cursor ?? undefined } }));
  const products = usePaged<Product>(textKey('products'), (cursor) => client.data(ops.listProducts, { path, query: { search: q, limit: PAGE, cursor: cursor ?? undefined } }));

  const rows = docs.items.map((d) => (workspace.getDraft(entityKey('document', d.id))?.current as unknown as Document | undefined) ?? d);
  // the same staged deletion as the grid and the editor (section 6.1): no dialog, a toast with Undo
  const stageDelete = (d: Document) => {
    const server = docs.items.find((x) => x.id === d.id) ?? d;
    stageDeletion(workspace, toasts, [{ id: d.id, base: baseOf(server), label: catalog.label(d), template: d.template }]);
  };

  return (
    <section className="search-page">
      <div className="page-head">
        <h1>Search</h1>
        {q && (
          <span className="page-meta">
            for <code>{q}</code>
          </span>
        )}
      </div>
      <form
        className="page-toolbar search-bar"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          searchBox.applyNow();
        }}
      >
        <span className="search-field">
          <SearchIcon className="search-field-icon" aria-hidden="true" />
          <TextInput value={searchBox.text} onChange={(e) => searchBox.setText(e.target.value)} placeholder="Ids, names, string keys, asset names, products…" aria-label="Search the branch" className="search-input" />
        </span>
        <Select className="input-inline" aria-label="Template filter" value={templateFilter ?? ''} onChange={(e) => qParam.set({ template: e.target.value || null })}>
          <option value="">Any template</option>
          {catalog.templateList
            .filter((t) => t.kind === 'document' || t.kind === 'singleton' || t.kind === 'component')
            .map((t) => (
              <option key={t.name} value={t.name}>
                {t.displayName ?? t.name}
              </option>
            ))}
        </Select>
        <Select className="input-inline" aria-label="Tag filter" value={tagFilter ?? ''} onChange={(e) => qParam.set({ tag: e.target.value || null })}>
          <option value="">Any tag</option>
          {catalog.tags.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name}
            </option>
          ))}
          {tagFilter && !catalog.tags.some((t) => t.name === tagFilter) && <option value={tagFilter}>{tagFilter}</option>}
        </Select>
      </form>

      {!docsKey ? (
        <EmptyState icon={<SearchIcon />} title="Search the branch" body="Type a text (the results follow as you type), or pick a template or a tag." />
      ) : (
        <>
          <section aria-label="Documents" className="search-section">
            <div className="section-head">
              <h2>Documents</h2>
              {!docs.loading || rows.length > 0 ? (
                <span className="chip">
                  {rows.length}
                  {docs.hasMore ? '+' : ''}
                </span>
              ) : null}
            </div>
            <DataGrid<Document>
              id="search:documents"
              rows={rows}
              rowKey={(d) => d.id}
              loading={docs.loading}
              revision={version + catalog.version}
              caption={`${rows.length}${docs.hasMore ? '+' : ''} document${rows.length === 1 ? '' : 's'}`}
              hasMore={docs.hasMore}
              onLoadMore={docs.loadMore}
              pageSize={DOC_PAGE}
              pageResetKey={docsKey}
              onRowOpen={(d) => docParam.open(d.id)}
              emptyState={
                docs.error ? (
                  <EmptyState icon={<AlertCircle />} title="The search failed" body={`${docs.error.message} (request ${docs.error.requestId})`} />
                ) : (
                  <EmptyState icon={<FileText />} title="No document matches" body="Try another text, or remove the template or tag filter." />
                )
              }
              columns={[
                {
                  key: 'id',
                  header: 'ID',
                  fixed: true,
                  width: 150,
                  render: (d) => {
                    const draft = workspace.getDraft(entityKey('document', d.id));
                    return (
                      <span className="row">
                        <button type="button" className="search-id-button" onClick={() => docParam.open(d.id)} aria-label={`Open ${catalog.label(d)}`}>
                          <code>{d.unnyId ?? d.id.slice(0, 8)}</code>
                        </button>
                        {draft && <Badge tone={draftTone[draft.status]}>{draft.status === 'deleted' ? 'DEL' : draft.status === 'new' ? 'NEW' : 'MOD'}</Badge>}
                      </span>
                    );
                  },
                  cellText: (d) => d.unnyId ?? d.id,
                },
                { key: 'label', header: 'Label', render: (d) => catalog.label(d), cellText: (d) => catalog.label(d), sortValue: (d) => catalog.label(d) },
                { key: 'template', header: 'Template', render: (d) => <Link to={routes.documents(org, gameSlug, branch, d.template)}>{catalog.templates.get(d.template)?.displayName ?? d.template}</Link>, cellText: (d) => d.template, sortValue: (d) => d.template },
                { key: 'tags', header: 'Tags', render: (d) => d.tags.join(', '), cellText: (d) => d.tags.join(', ') },
                { key: 'version', header: 'Version', align: 'right', render: (d) => (isTempId(d.id) ? '—' : String(d.version)), sortValue: (d) => Number(d.version) },
                {
                  key: 'menu',
                  header: '',
                  width: 40,
                  fixed: true,
                  render: (d) => (
                    <DocMenu
                      label={d.unnyId ?? d.id.slice(0, 8)}
                      items={[
                        { key: 'open', label: 'Open', onSelect: () => docParam.open(d.id) },
                        { key: 'grid', label: 'Open in its grid', onSelect: () => navigate(`${routes.documents(org, gameSlug, branch, d.template)}?doc=${encodeURIComponent(d.id)}`) },
                        {
                          key: 'clone',
                          label: 'Clone',
                          disabled: !canWrite,
                          onSelect: () => {
                            const id = cloneDocument(workspace, catalog, d);
                            toasts.success('Copy staged.');
                            docParam.open(id);
                          },
                        },
                        { key: 's', label: '', separator: true },
                        { key: 'delete', label: 'Delete', danger: true, disabled: !canWrite, onSelect: () => void stageDelete(d) },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </section>
          {q && (
            <div className="search-results-grid">
              <ResultSection title="Templates" icon={<Layers />} paged={templates} empty="No template matches." render={(t: Template) => (
                <div key={t.name} className="list-row static">
                  <Layers className="search-row-icon" aria-hidden="true" />
                  <span className="text">
                    <span className="title">
                      <Link to={routes.template(org, gameSlug, branch, t.name)}>{t.displayName ?? t.name}</Link>
                    </span>
                    <code className="sub">{t.name}</code>
                  </span>
                  <Badge>{KIND_LABEL[t.kind]}</Badge>
                  <Link className="small search-row-link" to={routes.documents(org, gameSlug, branch, t.name)}>
                    documents ({Number(t.documentCount)})
                  </Link>
                </div>
              )} />
              <ResultSection title="Localization strings" icon={<Languages />} paged={strings} empty="No string matches." render={(s: LocalizationString) => (
                <div key={s.id} className="list-row static">
                  <Languages className="search-row-icon" aria-hidden="true" />
                  <span className="text">
                    <span className="title">
                      <Link to={`${routes.branch(org, gameSlug, branch, 'localization/strings')}?string=${encodeURIComponent(s.id)}`}>
                        <code>{s.fullKey}</code>
                      </Link>
                    </span>
                    <span className="sub">{catalog.stringText(s.id) ?? Object.values(s.values ?? {})[0] ?? ''}</span>
                  </span>
                </div>
              )} />
              <ResultSection title="Assets" icon={<Images />} paged={assets} empty="No asset matches." render={(a: Asset) => (
                <div key={a.id} className="list-row static">
                  <Images className="search-row-icon" aria-hidden="true" />
                  <span className="text">
                    <span className="title">
                      <Link to={`${routes.branch(org, gameSlug, branch, 'assets/files')}?asset=${encodeURIComponent(a.id)}`}>{a.name}</Link>
                    </span>
                  </span>
                  <Badge>{a.kind}</Badge>
                </div>
              )} />
              <ResultSection title="Products" icon={<ShoppingBag />} paged={products} empty="No product matches." render={(p: Product) => (
                <div key={p.id} className="list-row static">
                  <ShoppingBag className="search-row-icon" aria-hidden="true" />
                  <span className="text">
                    <span className="title">
                      <Link to={`${routes.branch(org, gameSlug, branch, 'products')}?product=${encodeURIComponent(p.id)}`}>{productLabelOf(p)}</Link>
                    </span>
                    <code className="sub">{p.itemId}</code>
                  </span>
                </div>
              )} />
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ResultSection<T>({ title, icon, paged, render, empty }: { title: string; icon: ReactNode; paged: Paged<T>; render: (item: T) => ReactNode; empty: string }) {
  return (
    <section aria-label={title} className="search-section pane">
      <div className="pane-head">
        <span className="search-section-icon" aria-hidden="true">
          {icon}
        </span>
        <h2>{title}</h2>
        {(!paged.loading || paged.items.length > 0) && (
          <span className="chip">
            {paged.items.length}
            {paged.hasMore ? '+' : ''}
          </span>
        )}
      </div>
      {paged.error ? (
        <div className="pane-body">
          <div className="search-alert" role="alert">
            <AlertCircle className="search-alert-icon" aria-hidden="true" />
            <span>
              {paged.error.message} (request {paged.error.requestId})
            </span>
          </div>
        </div>
      ) : paged.items.length === 0 ? (
        paged.loading ? (
          <div className="skeleton-rows" role="status" aria-label="Searching…">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        ) : (
          <p className="pane-body muted small search-empty">{empty}</p>
        )
      ) : (
        <div className="list-pane search-results">{paged.items.map(render)}</div>
      )}
      {paged.hasMore && (
        <div className="pane-foot">
          <Button size="s" busy={paged.loading} onClick={paged.loadMore}>
            Load more
          </Button>
        </div>
      )}
    </section>
  );
}
function DocMenu({ label, items }: { label: string; items: { key: string; label: string; onSelect?: () => void; disabled?: boolean; separator?: boolean; danger?: boolean }[] }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton label={`Actions for ${label}`} aria-haspopup="menu" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Document actions" items={items} />
    </>
  );
}
